// @ts-nocheck
import * as React from "react";
import { ManualShell, Section, Lead, Steps, Note, GoTo, Shot, FlowStrip } from "./manual-shell";
import { supportPhone } from "./common";

// 실제 화면 스크린샷 경로 — npm run manual:shots 로 생성(public/manual-shots/region/).
const S = (key) => `/manual-shots/region/${key}.png`;

// 권역 관리자 사용자 매뉴얼 — 인앱 가이드.
// 진입점은 권역 대시보드 Topbar 우측 상단의 "사용자 매뉴얼" 버튼(dashboard-region.tsx).
// 콘텐츠는 region_admin 의 실제 권한·엔드포인트·케이스 수명주기에 맞춰 작성했다.
// 공용 셸/프리미티브(ManualShell·Section·Steps·Note·GoTo)는 manual-shell.tsx 에서 가져온다.

const TOC = [
  { id: "start",     label: "1. 시작하기" },
  { id: "dashboard", label: "2. 권역 대시보드" },
  { id: "approval",  label: "3. 승인 관리" },
  { id: "cod",       label: "4. AI 자동매칭(COD)" },
  { id: "resources", label: "4-1. 자원 조건검색(자원은행)" },
  { id: "cases",     label: "5. 케이스 관리" },
  { id: "schedule",  label: "6. 일정 관리" },
  { id: "reports",   label: "7. 방문 기록 검토" },
  { id: "drilldown", label: "8. 어르신·케이스 상세" },
  { id: "directory", label: "9. 권역·기관·매니저 조회" },
  { id: "report",    label: "10. 리포트" },
  { id: "alerts",    label: "11. 알림 처리" },
  { id: "policy",    label: "12. 권한·권역 격리 정책" },
  { id: "faq",       label: "13. 자주 묻는 질문 · 문의" },
];

const INTRO = (
  <>
    이 매뉴얼은 권역 관리자가 자신의 권역에서 수행하는 운영 업무를 화면 흐름에 맞춰 안내합니다.
    케어 신청 승인부터 매칭·배정, 방문 일정·기록 관리, 리포트까지 케어 수명주기 전반을 다룹니다.
    모든 데이터는 <b>본인 권역으로만</b> 제한되며(승인 큐도 권역 스코프), 자세한 권한 범위는 12장에서 설명합니다.
  </>
);

function RegionManual() {
  return (
    <ManualShell
      role="region"
      topbarTitle="사용자 매뉴얼"
      topbarSub="권역 관리자 가이드"
      badge="REGION ADMIN GUIDE"
      title="권역 관리자 사용자 매뉴얼"
      revised="최종 개정 2026-06-19 · 권역 관리자(region_admin) 기준"
      intro={INTRO}
      toc={TOC}
    >
      <Section id="start" title="1. 시작하기">
        <Lead>권역 관리자는 하나의 <b>권역(region)</b>을 책임지는 운영자입니다. 로그인하면 자동으로 권역 대시보드(/dashboard/region)로 이동합니다.</Lead>
        <Steps items={[
          "로그인 화면에서 발급받은 관리자 계정으로 접속합니다. (관리자 계정은 다단계 인증 권장)",
          "좌측 사이드바에서 권역 대시보드·AI 자동매칭·지도·일정·승인·권역/기관/매니저·리포트 화면으로 이동할 수 있습니다.",
          "화면 우측 상단의 검색창으로 어르신·케어매니저·기관을 빠르게 찾을 수 있습니다.",
          "도움이 필요할 때는 권역 대시보드 우측 상단의 '사용자 매뉴얼' 버튼으로 언제든 이 화면에 돌아옵니다.",
        ]} />
        <GoTo links={[{ href: "/dashboard/region", label: "권역 대시보드 열기" }]} />
      </Section>

      <Section id="dashboard" title="2. 권역 대시보드">
        <Lead>권역의 운영 현황을 한눈에 봅니다. KPI 카드, 케이스 단계별 잔여 건수(펑널), 실시간 위험 알림이 표시됩니다.</Lead>
        <Steps items={[
          "상단 기간 선택(주간/월간 등)을 바꾸면 신규 케이스·매칭 완료·방문 일정 지표가 함께 갱신됩니다.",
          "케이스 펑널의 단계를 클릭하면 해당 단계의 케이스 목록이 모달로 열립니다. 행을 클릭하면 케이스 상세로, '전체 케이스 목록에서 보기'로 케이스 목록 화면(해당 상태로 필터)으로 이동합니다. ('매칭' 단계만 곧장 COD 화면으로 연결)",
          "실시간 알림 패널은 새 위험 신호가 도착하면 30초 폴링을 기다리지 않고 즉시 갱신됩니다.",
          "데이터를 내보내려면 대시보드 CSV 내보내기를 사용합니다. (권역 단위 집계)",
        ]} />
        <Note>대시보드는 항상 <b>본인 권역</b>으로 고정됩니다. URL의 권역 코드(?code=)로 다른 권역을 보는 것은 플랫폼/시스템 관리자만 가능합니다.</Note>
        <Shot src={S("dashboard-region")} alt="권역 대시보드 화면" caption="권역 대시보드 — KPI 카드, 케이스 펑널, 실시간 알림 패널 (클릭하면 원본 크기로 열림)" />
        <GoTo links={[{ href: "/dashboard/region", label: "권역 대시보드 열기" }]} />
      </Section>

      <Section id="approval" title="3. 승인 관리">
        <Lead>어르신·보호자가 올린 케어 신청을 검토해 승인하거나 반려합니다. 승인하면 케이스 생성 단계로 이어집니다.</Lead>
        <Steps items={[
          "승인 관리 화면에서 대기 중인 신청 목록을 확인합니다.",
          "신청 상세(어르신 정보·신청 사유)를 검토합니다.",
          "적합하면 '승인', 보완이 필요하면 사유와 함께 '반려'합니다.",
          "승인·반려 결과는 신청자에게 알림으로 전달되고, 모든 처리는 감사 로그에 기록됩니다.",
        ]} />
        <Note>승인 큐는 <b>본인 권역 신청건만</b> 보입니다(다른 운영 데이터와 동일한 권역 격리). 소속 변경·유관기관 가입·기관 협약처럼 권역이 없는 플랫폼급 신청은 시스템 관리자만 처리합니다.</Note>
        <Shot src={S("approval")} alt="승인 관리 화면" caption="승인 관리 — 좌측 신청 목록에서 건을 선택하면 우측에서 승인/반려를 처리합니다." />
        <GoTo links={[{ href: "/approval", label: "승인 관리 열기" }]} />
      </Section>

      <Section id="cod" title="4. AI 자동매칭(COD)">
        <Lead>케이스에 적합한 케어매니저 후보를 AI가 제안합니다. 권역 관리자는 후보의 차원별 점수와 설명(XAI)을 검토하고 최종 배정을 결정합니다.</Lead>
        <Steps items={[
          "AI 자동매칭(COD) 화면의 매칭 인박스에서 결정 대기 중인 케이스를 봅니다.",
          "제안된 후보 매니저를 6개 차원(아래)으로 비교하고, 후보 카드의 'AI 평가 결과(차원별 분석)'에서 점수 근거를 확인합니다.",
          "후보가 부족하면 '후보 재생성 ↻'을, 기준을 바꿔보려면 '가중치 조정'으로 순위 변화를 미리 봅니다.",
          "각 후보에 대해 승인(accept)·거절(reject)·보류(hold)를 선택합니다. 승인하면 케이스가 배정(assigned) 상태로 넘어갑니다.",
        ]} />
        <Lead>매칭 점수는 다음 6개 차원의 가중 합산입니다. 괄호 안은 기본 가중치이며, '가중치 조정'으로 권역 운영 기준에 맞춰 바꿀 수 있습니다.</Lead>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.85 }}>
          <li><b>거리(25%)</b> — 매니저·어르신의 위치 핀 좌표를 기준으로 한 이동 거리. 좌표가 없으면 중립 처리(거리 미상)됩니다.</li>
          <li><b>자격(20%)</b> — 어르신에게 <b>필요한 돌봄 영역</b>과 매니저가 보유한 자격의 적합도(need-aware)입니다.</li>
          <li><b>가용(15%)</b> — 이번 주 근무시간의 여유. 잔여 시간이 많을수록 높으며, <b>취소된 방문은 사용 시간에서 제외</b>됩니다.</li>
          <li><b>부하(15%)</b> — 현재 담당 케이스 수. 적을수록 신규 케이스에 집중할 여력이 큽니다.</li>
          <li><b>과거관계(15%)</b> — 이 어르신과의 과거 방문·케이스 이력(실데이터). 이력이 있을수록 높습니다.</li>
          <li><b>평가(10%)</b> — 보호자·어르신이 남긴 <b>실제 별점 평가</b> 집계입니다. 리뷰가 없으면 초기 평점으로 표기됩니다.</li>
        </ul>
        <Note>AI 매칭 결과는 <b>최종 결정의 보조 수단</b>입니다. 어르신 상태와 매니저 사정을 함께 고려해 결정하세요. 가중치 조정은 후보 순위를 미리 보는 용도이며, 실제 적용은 '후보 재생성'으로 반영됩니다.</Note>
        <Note><b>직접 배정(점수 우회):</b> 부득이하게 AI 점수를 우회해 특정 매니저에게 케이스를 직접 배정하려면 케어매니저 관리(9장) 화면의 '직접 배정'을 사용합니다(can_manage_cases 필요). 이 경우 <b>배정 사유 입력이 필수</b>이며 감사 로그(5년 보존)에 남습니다. 기본 배정 경로는 점수·근거 기반의 AI 자동매칭입니다.</Note>
        <Shot src={S("cod")} alt="AI 자동매칭(COD) 화면" caption="AI 자동매칭(COD) — 매칭 인박스에서 6개 차원으로 후보를 비교하고 승인/거절/보류를 결정합니다." />
        <GoTo links={[{ href: "/cod", label: "AI 자동매칭 열기" }, { href: "/managers", label: "케어매니저 관리(직접 배정)" }]} />
      </Section>

      <Section id="resources" title="4-1. 자원 조건검색(자원은행)">
        <Lead>케어매니저가 아니라 <b>기관 급여(서비스)</b>를 찾는 화면입니다. 어떤 기관이 지금 실제로 모실 수 있는지를 조건으로 좁혀 후보를 3분 안에 확인하고, 그대로 <b>서비스 의뢰</b>를 보냅니다.</Lead>
        <Steps items={[
          "자원 조건검색 화면에서 필요한 급여(서비스 종류)와 지역(행정동)을 고릅니다.",
          "필수 조건(예: 야간 가능)과 선호 조건, 요청일을 지정합니다. 선호 조건을 비워도 순위가 깎이지 않습니다.",
          "거리를 순위에 반영하려면 지도에서 기준 위치 핀을 찍습니다. 핀이 없으면 거리 차원은 '—'로 표시되고 적용되지 않습니다.",
          "후보 목록에서 기관을 고르고 '의뢰 보내기'로 서비스 의뢰를 발송합니다. 발송 즉시 해당 기관의 '받은 의뢰' 인박스에 들어갑니다.",
        ]} />
        <Lead>기관 순위는 다음 4개 차원의 가중 합산이며, 화면이 그 기준을 그대로 보여 줍니다.</Lead>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.85 }}>
          <li><b>가용(40%)</b> — 기관이 직접 보고한 여유 정원. 갱신이 오래된 값은 신선도에서 감점됩니다.</li>
          <li><b>조건(20%)</b> — 필수·선호 조건 충족도. 커버리지 동을 등록하지 않은 기관은 '제한 없음'으로 통과합니다.</li>
          <li><b>신선도(20%)</b> — 가용성 보고가 얼마나 최근인지. 오래되면 STALE로 표시됩니다.</li>
          <li><b>거리(20%)</b> — 기준 위치 핀과 기관 사이 거리. 핀을 찍지 않으면 적용되지 않습니다.</li>
        </ul>
        <Note><b>대상자 정보는 검색에 들어가지 않습니다.</b> 조건검색은 어르신·케이스 식별자를 받지 않으며, 자원과 대상자가 결합되는 지점은 <b>서비스 의뢰 한 곳뿐</b>입니다. 기관에 전달되는 대상자 정보도 수락 판단에 필요한 최소치(이름·나이·주소·필요 급여)로 제한됩니다.</Note>
        <Note>의뢰 상태는 <b>대기 → 연락 → 수용 또는 거절</b>로 흐릅니다. 거절에는 사유가 필수이고, 같은 케이스·같은 급여로 열린 의뢰가 있으면 중복 발송이 차단됩니다. 취소는 보낸 쪽(운영자)만 할 수 있습니다.</Note>
        <GoTo links={[{ href: "/resources", label: "자원 조건검색 열기" }]} />
      </Section>

      <Section id="cases" title="5. 케이스 관리">
        <Lead>케이스는 매칭부터 종결까지 5단계를 거칩니다(접수 시 매칭을 미루면 "보류" 단계가 선행). 권역 관리자는 각 단계에 개입해 우선순위를 조정하고 진행을 관리합니다.</Lead>
        <p style={{ margin: "12px 0", padding: "10px 14px", background: "var(--surface)", border: "1px solid var(--border-soft)", borderRadius: 10, fontSize: 12.5, fontWeight: 700, color: "var(--ink-1)" }}>
          보류(applying) → 매칭(matching) → 배정(assigned) → 돌봄(caring) → 종결(closed)
        </p>
        <Lead>실제 화면 흐름 — 신청 승인부터 방문까지 권역 관리자가 거치는 화면 순서입니다. 썸네일을 클릭하면 원본 화면이 열립니다.</Lead>
        <FlowStrip steps={[
          { src: S("approval"), label: "승인 관리" },
          { src: S("cod"),      label: "AI 자동매칭" },
          { src: S("schedule"), label: "일정 관리" },
          { src: S("map"),      label: "지도·방문" },
        ]} />
        <Steps items={[
          "신청 승인 후 생성된 케이스의 어르신 상태를 검토하고 우선순위를 설정합니다. (높음 critical / 보통 warning / 낮음 normal)",
          "매칭 단계에서는 COD 화면으로 후보를 결정합니다(4장 참고).",
          "배정 이후 돌봄이 진행되는 동안 방문 일정과 기록을 모니터링합니다.",
          "종결 시 매니저가 사유(회복·입원·사망·전출·거절·이관 등)를 입력하면 케이스가 닫히고 통보됩니다.",
        ]} />
        <Lead>좌측 사이드바 '케이스 목록'을 열면 권역의 <b>모든 케이스를 한 화면</b>에서 봅니다. 어르신 이름으로 검색하고 상태(진행 중/단계별)·우선순위로 필터링한 뒤, 행을 클릭해 케이스·방문 상세로 드릴다운합니다.</Lead>
        <Lead>케이스 목록 화면 <b>우측 상단의 '신규 케이스 등록'</b> 버튼으로 케이스를 직접 만들 수 있습니다(can_manage_cases). 어르신을 선택하고 우선순위·시작 단계(매칭/보류)·메모를 입력해 저장하면 곧바로 매칭 단계로 진행됩니다. 진행 중 케이스가 있는 어르신과 승인 대기 중인 어르신은 중복 등록 방지를 위해 목록에서 제외됩니다.</Lead>
        <Note>케이스는 보통 <b>케어 신청 승인</b>을 거쳐 자동 생성되지만(3장), 신청 절차 밖에서 발생한 건은 이 '신규 케이스 등록'으로 운영자가 직접 등록합니다. (이 버튼은 이전에 권역 대시보드 상단에 있었으나, 케이스 작업 동선에 맞춰 <b>케이스 목록 화면으로 이동</b>했습니다.)</Note>
        <Shot src={S("cases")} alt="권역 케이스 목록 화면" caption="권역 케이스 목록 — 우측 상단 '신규 케이스 등록'으로 케이스를 직접 만들고, 상태·우선순위로 검색해 행을 클릭하면 상세로 드릴다운합니다." />
        <GoTo links={[{ href: "/cases", label: "케이스 목록" }, { href: "/case", label: "케이스 상세" }]} />
      </Section>

      <Section id="schedule" title="6. 일정 관리">
        <Lead>권역 내 방문 일정을 조회하고 필요 시 조정합니다.</Lead>
        <Steps items={[
          "일정 관리 화면에서 날짜별 방문 일정을 확인합니다.",
          "방문을 추가하거나 시간·담당 매니저를 수정합니다.",
          "사정이 생기면 방문을 재배정하거나 취소할 수 있습니다.",
          "일정 변경은 관련 매니저·보호자에게 알림으로 전달됩니다.",
        ]} />
        <Shot src={S("schedule")} alt="일정 관리 화면" caption="일정 관리 — 날짜별 방문 일정을 조회·조정합니다." />
        <GoTo links={[{ href: "/schedule", label: "일정 관리 열기" }, { href: "/map", label: "지도에서 보기" }]} />
      </Section>

      <Section id="reports" title="7. 방문 기록 검토">
        <Lead>케어매니저가 작성한 방문 기록을 열람하고 검토합니다.</Lead>
        <Steps items={[
          "케이스·방문 상세에서 방문 기록을 엽니다.",
          "기록 내용(어르신 상태·활력징후·특이사항)을 확인합니다.",
          "보정이 필요하면 기록을 수정합니다. (권역 관리자는 검토·수정 권한 보유)",
        ]} />
        <GoTo links={[{ href: "/visit", label: "방문 상세 열기" }]} />
      </Section>

      <Section id="drilldown" title="8. 어르신·케이스 상세">
        <Lead>목록에서 개별 어르신으로 들어가 케이스·방문 이력을 깊이 있게 봅니다.</Lead>
        <Steps items={[
          "케이스 목록 또는 대시보드 펑널 드릴다운에서 어르신을 선택합니다.",
          "어르신 상세에서 기본 정보·케이스 진행 상황을 확인합니다.",
          "케이스 상세 → 방문 상세로 단계적으로 드릴다운해 세부 내용을 봅니다.",
          "위험등급(위험/주의/안정)을 조정하려면 위험등급 카드 옆 '변경'을 눌러 새 등급과 사유를 입력합니다(can_manage_elderly_risk · 본인 권역 한정).",
        ]} />
        <Note>위험등급 변경은 <b>사유 입력이 필수</b>이며 감사 로그(5년 보존)에 남습니다. 케이스 우선순위(높음/보통/낮음)와 어르신 위험등급(위험/주의/안정)은 서로 다른 개념입니다 — 우선순위는 케이스 처리 순서, 위험등급은 어르신 상태를 뜻합니다.</Note>
        <GoTo links={[{ href: "/elderly", label: "어르신 상세" }, { href: "/cases", label: "케이스 목록" }]} />
      </Section>

      <Section id="directory" title="9. 권역·기관·매니저 관리">
        <Lead>본인 권역의 유관기관을 직접 등록·관리하고, 권역·케어매니저 정보를 조회합니다.</Lead>
        <Steps items={[
          "유관기관 화면에서 본인 권역의 협력 기관(소방·병원·보건소 등)을 등록·수정·삭제합니다(can_manage_organizations · 본인 권역 한정). 새 기관 등록 시 소속 권역은 본인 권역으로 자동 고정됩니다.",
          "'CSV 내보내기'로 자기 권역 기관 목록을 받을 수 있습니다(can_export_orgs · 자기 권역 한정).",
          "케어매니저 화면에서 소속 매니저의 현황을 확인하고, 필요 시 매칭 단계 케이스를 '직접 배정'합니다(can_manage_cases · 사유 필수, 4장 참고).",
          "권역 관리 화면에서 권역 정보를 열람합니다.",
        ]} />
        <Note>기관의 <b>병합·일괄 변경·통계 재집계</b>는 전 권역에 영향을 주는 작업이라 시스템 관리자 전용입니다(UI에서도 권역 관리자에게는 숨겨집니다). <b>권역·케어매니저 계정의 생성·수정·삭제</b>와 권역·매니저 목록 CSV 내보내기 역시 시스템 관리자 권한입니다(12장 참고). 권역 관리자가 받을 수 있는 CSV는 <b>유관기관 CSV</b>(자기 권역 한정)이며, 모든 CSV는 Excel 한글 깨짐 방지를 위해 UTF-8(BOM)로 제공됩니다.</Note>
        <Shot src={S("organizations")} alt="유관기관 화면" caption="유관기관 — 본인 권역의 협력 기관을 등록·수정·삭제하고 CSV로 내보냅니다." />
        <Shot src={S("managers")} alt="케어매니저 목록 화면" caption="케어매니저 — 소속 매니저의 자격·연락처를 조회합니다." />
        <GoTo links={[{ href: "/regions", label: "권역 관리" }, { href: "/organizations", label: "유관기관" }, { href: "/managers", label: "케어매니저" }]} />
      </Section>

      <Section id="members" title="9-1. 회원 관리">
        <Lead>본인 권역의 어르신·보호자 회원을 탈퇴 처리·재활성화합니다(can_manage_members). 권역 관리자는 자기 권역 회원만 대상입니다(교차권역 차단).</Lead>
        <Steps items={[
          "회원 관리 화면에서 권역 회원을 검색·필터링해 조회합니다.",
          "회원을 탈퇴 처리하면 즉시 로그인이 차단되고(state=off), 유예 기간 후 개인정보가 익명화됩니다.",
          "탈퇴한 회원을 유예 기간 내 재활성화하면 로그인이 복구됩니다.",
        ]} />
        <Note>모든 탈퇴·재활성화는 감사 로그에 기록됩니다.</Note>
        <GoTo links={[{ href: "/members", label: "회원 관리 열기" }]} />
      </Section>

      <Section id="account" title="9-2. 내 계정 관리">
        <Lead>본인 관리자 계정의 정보를 직접 관리합니다. 좌측 사이드바 '내 계정' 또는 우측 상단 프로필에서 들어갑니다.</Lead>
        <Steps items={[
          "이름·연락처 등 기본 정보를 수정합니다.",
          "비밀번호를 변경합니다. (정기 변경 권장)",
          "프로필 사진을 등록·교체합니다.",
        ]} />
        <Note>내 계정 화면에서 바꾸는 것은 <b>본인 계정</b>에 한합니다. 다른 관리자·매니저 계정 관리는 시스템 관리자 권한입니다(12장 참고).</Note>
        <GoTo links={[{ href: "/account", label: "내 계정 열기" }]} />
      </Section>

      <Section id="report" title="10. 리포트">
        <Lead>권역 운영 통계를 생성하고 조회·다운로드합니다.</Lead>
        <Steps items={[
          "리포트 화면에서 기존 리포트를 조회합니다.",
          "필요한 기간·항목으로 새 리포트를 생성합니다.",
          "생성된 리포트 상세를 확인하고 공유·다운로드합니다.",
        ]} />
        <Shot src={S("report")} alt="리포트 화면" caption="리포트 — 권역 운영 통계를 생성·조회·다운로드합니다." />
        <GoTo links={[{ href: "/report", label: "리포트 열기" }]} />
      </Section>

      <Section id="alerts" title="11. 알림 처리">
        <Lead>어르신의 위험 신호 등 실시간 알림을 확인하고 해결 처리합니다.</Lead>
        <Steps items={[
          "권역 대시보드의 실시간 알림 패널 또는 상단 알림함에서 새 알림을 확인합니다.",
          "알림 상세를 열어 상황을 파악합니다.",
          "필요한 조치를 취한 뒤 '해결(resolve)' 처리하면 패널에서 정리됩니다.",
        ]} />
        <GoTo links={[{ href: "/dashboard/region", label: "대시보드 알림 패널" }]} />
      </Section>

      <Section id="policy" title="12. 권한·권역 격리 정책">
        <Lead>권역 관리자가 할 수 있는 일과 할 수 없는 일을 명확히 정리합니다.</Lead>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--green-600, #2F8F5B)" }}>할 수 있는 작업</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>본인 권역의 케이스·어르신·방문·기록 조회 및 관리(직접 배정 포함 — 사유 필수·감사 기록)</li>
          <li>케어 신청 승인/반려, 매칭 결정·가중치 조정, 방문 일정 조정, 알림 해결, 리포트 생성</li>
          <li>본인 권역 유관기관 등록·수정·삭제, 어르신 위험등급 변경(사유 필수·감사 기록)</li>
          <li>본인 권역 회원(어르신·보호자) 탈퇴/재활성화, 유관기관 CSV 내보내기(자기 권역 한정)</li>
          <li>본인 관리자 계정 정보·비밀번호·프로필 사진 수정</li>
        </ul>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--orange-700)" }}>할 수 없는 작업 (시스템 관리자 전용)</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>권역·케어매니저 계정의 생성·수정·삭제, 권역·케어매니저 목록 CSV 내보내기</li>
          <li>유관기관 <b>병합·일괄 변경·통계 재집계</b>(전 권역 영향), 타 관리자 계정 관리</li>
          <li>감사 로그 전체 조회, 시스템 설정·권한 매트릭스 변경</li>
        </ul>
        <Note><b>권역 격리:</b> 케이스·방문·기록·매니저·<b>승인 큐</b>가 모두 본인 권역으로 제한됩니다. 권역이 없는(플랫폼급) 승인건은 시스템 관리자 전용입니다(3장).</Note>
      </Section>

      <Section id="faq" title="13. 자주 묻는 질문 · 문의">
        <p style={{ margin: "10px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 다른 권역의 신청이 승인 목록에 보이지 않습니다.</p>
        <Lead>정상입니다. 승인 큐는 본인 권역으로 스코프됩니다. 권역이 지정되지 않은 플랫폼급 신청(소속 변경·기관 가입·협약)은 시스템 관리자가 처리합니다.</Lead>
        <p style={{ margin: "14px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 케어매니저를 새로 등록하려면?</p>
        <Lead>매니저 계정 생성은 시스템 관리자 권한입니다. 운영지원으로 요청해 주세요.</Lead>
        <p style={{ margin: "14px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. AI 매칭 후보가 적절하지 않습니다.</p>
        <Lead>COD 화면에서 후보를 재생성하거나 가중치를 조정해 다시 시뮬레이션할 수 있습니다(4장).</Lead>
        <div style={{ marginTop: 18, padding: 16, background: "var(--bg-sub)", borderRadius: 12, fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.8 }}>
          <div style={{ fontWeight: 800, color: "var(--ink-1)", marginBottom: 4 }}>운영지원 문의</div>
          support@care.local · {supportPhone()} (평일 09:00–18:00)
        </div>
      </Section>
    </ManualShell>
  );
}

export { RegionManual };
